import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { AccountDrawer } from './AccountDrawer';

const BRAND_LOGO = 'https://static.wixstatic.com/media/0a7e2f_c2a4619aa00e45a3b47edf0a50ab5cc0~mv2.jpg';

interface NavLinkItem {
  href: string;        // hash o ruta
  label: string;
  isRoute?: boolean;   // si true => Link, si false => <a href hash>
}

const LINKS: NavLinkItem[] = [
  { href: '/#inicio',       label: 'INICIO' },
  { href: '/catalogo',      label: 'PRODUCTOS', isRoute: true },
  { href: '/#historia',     label: 'NOSOTROS' },
  { href: '/sedes',         label: 'SEDES',     isRoute: true },
  { href: '/vacantes',      label: 'VACANTES',  isRoute: true },
  { href: '/#cotizacion',   label: 'COTIZAR' },
];

const ROLE_LABEL: Record<string, { text: string; cls: string }> = {
  admin:        { text: 'ADMIN',        cls: 'role-admin' },
  distribuidor: { text: 'DISTRIBUIDOR', cls: 'role-distribuidor' },
  cliente:      { text: 'CLIENTE',      cls: 'role-cliente' },
};

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isAuthed, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const initial = profile?.name?.charAt(0).toUpperCase() ?? '?';
  const role = profile ? ROLE_LABEL[profile.role] ?? ROLE_LABEL.cliente : null;

  /** Cuando el link es hash (#algo) y estamos fuera del Home, navegamos a "/" + hash. */
  function handleHashClick(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
    if (location.pathname !== '/') {
      e.preventDefault();
      const hash = href.startsWith('/#') ? href.slice(1) : href;
      navigate('/' + hash);
    }
    setMobileOpen(false);
  }

  function openLogin() {
    setMobileOpen(false);
    navigate('/login');
  }

  return (
    <>
      <nav id="nav" className={scrolled ? 'scrolled' : ''}>
        <div className="nav-inner">
          <Link to="/#inicio" className="nav-brand">
            <img src={BRAND_LOGO} alt="Las Polvillas" />
            <span className="nav-brand-name">LAS <em>POLVILLAS</em></span>
          </Link>

          <ul className="nav-links">
            {LINKS.map((l) =>
              l.isRoute ? (
                <li key={l.href}><Link to={l.href}>{l.label}</Link></li>
              ) : (
                <li key={l.href}>
                  <a href={l.href} onClick={(e) => handleHashClick(e, l.href)}>{l.label}</a>
                </li>
              ),
            )}
          </ul>

          <div className="nav-auth">
            {isAuthed && profile ? (
              <button className="nav-user" onClick={() => setDrawerOpen(true)} aria-label="Abrir mi cuenta">
                <div className="nav-user-avatar">{initial}</div>
                <div>
                  <div className="nav-user-name">{profile.name}</div>
                  {role && <span className={`role-badge ${role.cls}`}>{role.text}</span>}
                </div>
              </button>
            ) : (
              <button className="nav-login-btn" onClick={openLogin}>ENTRAR</button>
            )}
          </div>

          <button
            className={`burger ${mobileOpen ? 'open' : ''}`}
            aria-label="Menú"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
      </nav>

      <div className={`mobile-nav ${mobileOpen ? 'open' : ''}`}>
        {LINKS.map((l) =>
          l.isRoute ? (
            <Link key={l.href} to={l.href} onClick={() => setMobileOpen(false)}>{l.label}</Link>
          ) : (
            <a key={l.href} href={l.href} onClick={(e) => handleHashClick(e, l.href)}>{l.label}</a>
          ),
        )}
        {isAuthed ? (
          <a href="#mi-cuenta" style={{ color: 'var(--orange)' }}
             onClick={(e) => { e.preventDefault(); setMobileOpen(false); setDrawerOpen(true); }}>
            MI CUENTA
          </a>
        ) : (
          <a href="#login" style={{ color: 'var(--orange)' }}
             onClick={(e) => { e.preventDefault(); openLogin(); }}>
            ENTRAR
          </a>
        )}
      </div>

      <AccountDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
